import {Alert, Button, Group, Loader, Paper, Stack, Text, ThemeIcon, Title} from '@mantine/core'
import {IconCheck} from '@tabler/icons-react'
import {Link, useNavigate} from '@tanstack/react-router'

import {formatJobDate} from 'src/jobs/job-schedule-format'
import {useJob} from 'src/jobs/use-jobs'

export function JobEnteredPanel({
    jobId,
    addedToOrder = false,
}: {
    jobId: string
    addedToOrder?: boolean
}) {
    const navigate = useNavigate()
    const {data: job, isLoading, isError} = useJob(jobId)

    if (isLoading) {
        return <Loader aria-label="Loading the job" />
    }
    if (isError || !job) {
        return (
            <Alert color="yellow" title="Job created">
                Could not load job.{' '}
                <Link to="/jobs/$jobId" params={{jobId}}>
                    Open the job
                </Link>
            </Alert>
        )
    }

    return (
        <Paper withBorder p="xl" radius="md">
            <Stack gap="md" align="flex-start">
                <Group gap="sm">
                    <ThemeIcon color="green" radius="xl" size="lg">
                        <IconCheck size={20} />
                    </ThemeIcon>
                    <Title order={2}>Job {job.displayId} created</Title>
                </Group>
                <Text>
                    {job.name} for {job.customerName}, {formatJobDate(job.startDate)}
                    {job.endDate && job.endDate !== job.startDate
                        ? ` to ${formatJobDate(job.endDate)}`
                        : ''}
                    .
                </Text>
                <Text size="sm" c="dimmed">
                    {job.status === 'active'
                        ? 'Ready for dispatch.'
                        : 'Saved as planned. Dispatch sees it once it is marked ready.'}{' '}
                    {addedToOrder
                        ? `Added to order ${job.orderNumber ?? job.projectName ?? ''}. Dispatchers notified.`
                        : `Confirmation email sent to ${job.repName ?? 'the rep'}.`}
                </Text>
                <Group gap="sm">
                    <Button onClick={() => void navigate({to: '/jobs/$jobId', params: {jobId}})}>
                        Open job {job.displayId}
                    </Button>
                    {addedToOrder ? (
                        <Button
                            variant="default"
                            onClick={() =>
                                void navigate({
                                    to: '/projects/$projectId',
                                    params: {projectId: job.projectId},
                                })
                            }
                        >
                            Back to order
                        </Button>
                    ) : (
                        <Button
                            variant="default"
                            onClick={() => void navigate({to: '/jobs/create', search: {new: 1}})}
                        >
                            Enter another job
                        </Button>
                    )}
                    <Button variant="subtle" onClick={() => void navigate({to: '/jobs'})}>
                        All jobs
                    </Button>
                </Group>
            </Stack>
        </Paper>
    )
}
